import { ApiError } from '@/lib/api/errors';
import {
  formatDayMonthYear,
  formatHour,
  formatMonthDay,
  formatMonthYear,
  formatOrdinalDayMonthYear,
} from '@/lib/date';

import { DEVICE_ERROR_STATUS } from './constants';
import { FLOW_RATE_RANGE, type FlowRatePoint, type FlowRateRange } from './details-schemas';

// Pure display rules for the Device Details page. The AC is unusually precise about the chart
// axis and the summary number formats, so each rule lives here and is unit-tested rather than
// being buried in a component.

/** The chart always draws this many Y gridlines, including 0 (US-112437 §3). */
export const FLOW_RATE_GRIDLINES = 6;

/** With no data — or a peak of 0 — the axis falls back to 0…5 (§3). */
export const FLOW_RATE_FALLBACK_MAX = 5;

/** Ceiling on drawn X labels for the one range whose bucket count isn't fixed by the AC. */
export const FLOW_RATE_MAX_X_LABELS = 6;

/**
 * The Y-axis ticks for the flow-rate chart (US-112437 §3).
 *
 * The maximum is the range's peak **rounded up to the nearest multiple of 5**, the axis always
 * shows exactly 6 evenly-spaced points from 0, and the step is `max / 5`. All labels are whole
 * numbers with no repeats — which is why the max is also floored to a multiple of 5 *at least* as
 * large as `FLOW_RATE_GRIDLINES - 1`: a smaller max would repeat labels once rounded.
 */
export function flowRateAxis(points: FlowRatePoint[]): { max: number; ticks: number[] } {
  const peak = points.reduce((highest, point) => Math.max(highest, point.value), 0);
  const max = peak > 0 ? Math.ceil(peak / 5) * 5 : FLOW_RATE_FALLBACK_MAX;
  const step = max / (FLOW_RATE_GRIDLINES - 1);
  const ticks = Array.from({ length: FLOW_RATE_GRIDLINES }, (_, index) => index * step);
  return { max, ticks };
}

/**
 * Axis label for one bucket, derived from its instant per the selected range (§3):
 * hour for Today, `MMM d` for 7/30 days, `MMM yyyy` for 1 Year and All Time. A server-supplied
 * label always wins.
 *
 * The AC's example for 1 Year is "Jan 2025, Apr 2025, … Dec 2025" — month **and** year — because
 * a 12-month window straddles a year boundary and a bare "Jan" is ambiguous against "Dec".
 *
 * Formatting itself lives in `lib/date` per ADR-0007; this function only picks the shape.
 */
export function flowRateLabel(point: FlowRatePoint, range: FlowRateRange): string {
  if (point.label) return point.label;
  if (!point.at) return '';

  switch (range) {
    case FLOW_RATE_RANGE.TODAY:
      // "12 AM", "2 AM" … the design labels every other hour; the chart thins these itself.
      return formatHour(point.at) ?? '';
    case FLOW_RATE_RANGE.SEVEN_DAYS:
    case FLOW_RATE_RANGE.THIRTY_DAYS:
      return formatMonthDay(point.at) ?? '';
    case FLOW_RATE_RANGE.ONE_YEAR:
    case FLOW_RATE_RANGE.ALL_TIME:
      return formatMonthYear(point.at) ?? '';
    default:
      return '';
  }
}

/**
 * How many X labels recharts should skip between the ones it draws (§3).
 *
 * recharts' `interval` is a *skip* count, so "every Nth label" is `N - 1`. The AC fixes the
 * cadence per range rather than leaving it to fit-to-width:
 *
 * | range    | buckets | AC cadence                                  | interval |
 * |----------|---------|---------------------------------------------|----------|
 * | Today    | 24      | "Every 2 hours (12 AM, 2 AM, … 10 PM)"      | 1        |
 * | 7 Days   | 7       | "Day names or dates" — every bucket          | 0        |
 * | 30 Days  | 30      | "Every 5 days (Jul 1, Jul 5, Jul 10)"        | 4        |
 * | 1 Year   | 12      | "Month + year at intervals (Jan, Apr, … Dec)" | 2       |
 * | All Time | dynamic | "appropriate to history length"              | computed |
 *
 * All Time is the only range whose bucket count isn't known up front, so it keeps the
 * fit-to-width behaviour: thin to at most {@link FLOW_RATE_MAX_X_LABELS} labels.
 */
export function flowRateLabelInterval(range: FlowRateRange, bucketCount: number): number {
  switch (range) {
    case FLOW_RATE_RANGE.TODAY:
      return 1;
    case FLOW_RATE_RANGE.SEVEN_DAYS:
      return 0;
    case FLOW_RATE_RANGE.THIRTY_DAYS:
      return 4;
    case FLOW_RATE_RANGE.ONE_YEAR:
      return 2;
    default:
      if (bucketCount <= FLOW_RATE_MAX_X_LABELS) return 0;
      return Math.ceil(bucketCount / FLOW_RATE_MAX_X_LABELS) - 1;
  }
}

/** Tooltip heading: "15 Gallons / 12 Jan 2026" in the design (§3). */
export function flowRateTooltipDate(point: FlowRatePoint): string {
  return formatDayMonthYear(point.at) ?? point.label ?? '';
}

/**
 * "Up to 2 decimals, remaining rounded off" (§4) — and no trailing zeros, so 75.5 stays "75.5"
 * rather than becoming "75.50".
 */
export function formatUpTo2Decimals(value: number | null): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return String(Math.round(value * 100) / 100);
}

/**
 * Runtime as "7 hrs 24 min", with seconds rounded off (§4). Sub-hour runtimes drop the hours part
 * rather than reading "0 hrs 24 min".
 */
export function formatRuntime(totalMinutes: number | null): string | null {
  if (totalMinutes == null || !Number.isFinite(totalMinutes) || totalMinutes < 0) return null;
  const minutes = Math.round(totalMinutes);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  if (rest === 0) return `${hours} hrs`;
  return `${hours} hrs ${rest} min`;
}

/**
 * Countdown as `HH:MM:SS`, which is how the Stop Timer pill renders it (Figma 219:334140).
 * Takes **seconds**, matching the shared `useCountdown` hook that feeds it.
 */
export function formatCountdown(secondsRemaining: number): string {
  const total = Math.max(0, Math.floor(secondsRemaining));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

/**
 * How much of a stop timer is left, as a 0–1 fraction, for the ring around the power disc — full
 * at the start and emptying toward the stop time (§10).
 */
export function stopTimerProgress(
  timer: { stopsAt: string | null; durationMinutes: number | null },
  now: Date = new Date(),
): number | null {
  if (!timer.stopsAt || !timer.durationMinutes) return null;
  const stopsAt = Date.parse(timer.stopsAt);
  if (Number.isNaN(stopsAt)) return null;
  const totalMs = timer.durationMinutes * 60_000;
  if (totalMs <= 0) return null;
  const remaining = stopsAt - now.getTime();
  return Math.min(1, Math.max(0, remaining / totalMs));
}

/** "12th Jan 2026", the Warranty Valid Till format in the metadata card (§6). */
export function formatOrdinalDate(iso: string | null): string | null {
  return formatOrdinalDayMonthYear(iso);
}

/** Crop year band, e.g. "Jan 2025 - Jan 2026" (§5). Passes a server-formatted string through. */
export function formatCropYear(cropYear: string | null): string | null {
  if (!cropYear) return null;
  const year = Number(cropYear);
  if (!Number.isInteger(year)) return cropYear;
  return `Jan ${year} - Jan ${year + 1}`;
}

/**
 * How the Device Details page should present a failed detail read.
 *
 * The three outcomes are different in the AC and must not collapse into one: §13's *not found*
 * ("Device not found. This device may have been removed." — and the page returns to the listing),
 * §12's *access denied* for a device owned by another grower, and everything else, which is a
 * transient failure the grower can retry. Branching on `isError` alone printed the removed-device
 * copy for a 500 and for a dropped connection too.
 *
 * A non-`ApiError` (a network failure never reaches the server) is `failed`, which is correct:
 * nothing has been said about whether the device exists.
 */
export const DETAILS_ERROR = {
  NOT_FOUND: 'not-found',
  FORBIDDEN: 'forbidden',
  FAILED: 'failed',
} as const;

export type DetailsError = (typeof DETAILS_ERROR)[keyof typeof DETAILS_ERROR];

export function detailsErrorKind(error: unknown): DetailsError {
  if (!(error instanceof ApiError)) return DETAILS_ERROR.FAILED;
  if (error.status === DEVICE_ERROR_STATUS.NOT_FOUND) return DETAILS_ERROR.NOT_FOUND;
  if (error.status === DEVICE_ERROR_STATUS.FORBIDDEN) return DETAILS_ERROR.FORBIDDEN;
  return DETAILS_ERROR.FAILED;
}
